/**
 * Netzzustand.
 *
 * `navigator.onLine` allein genügt nicht: Der Wert ist im Kellergeschoss und in
 * WLANs mit Anmeldeseite regelmäßig `true`, obwohl kein Server erreichbar ist.
 * Deshalb zusätzlich eine eigene Erreichbarkeitsprüfung gegen `/health/live`
 * (ADR 0001, Zeile „Netzstatus").
 */

export type Reachability = "unknown" | "online" | "offline";

let lastProbe: { at: number; result: boolean } | null = null;
const PROBE_CACHE_MS = 5_000;

const listeners = new Set<(online: boolean) => void>();
let bound = false;

function notify(): void {
  const value = navigator.onLine;
  for (const listener of listeners) listener(value);
}

function bind(): void {
  if (bound) return;
  window.addEventListener("online", notify);
  window.addEventListener("offline", notify);
  bound = true;
}

/** Grobe Auskunft des Betriebssystems. Schnell, aber nicht verlässlich. */
export function isOnline(): boolean {
  return navigator.onLine;
}

export function subscribeNetwork(listener: (online: boolean) => void): () => void {
  bind();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Echte Erreichbarkeitsprüfung. Ergebnis wird kurz gemerkt, damit ein Durchlauf
 * der Warteschlange mit zwanzig Einträgen nicht zwanzig Prüfungen auslöst.
 */
export async function probe(timeoutMs = 3000): Promise<boolean> {
  if (!navigator.onLine) return false;

  const now = Date.now();
  if (lastProbe && now - lastProbe.at < PROBE_CACHE_MS) return lastProbe.result;

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  try {
    const response = await fetch("/health/live", {
      method: "GET",
      // Kein Zwischenspeicher: Eine gemerkte Antwort würde behaupten, der
      // Server sei erreichbar, während das Gerät im Funkloch steht.
      cache: "no-store",
      signal: abort.signal,
    });
    const result = response.ok;
    lastProbe = { at: now, result };
    return result;
  } catch {
    lastProbe = { at: now, result: false };
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** Verwirft das gemerkte Prüfergebnis — nach einem Wechsel des Netzzustands. */
export function invalidateProbe(): void {
  lastProbe = null;
}
